import { MonetaryValue } from '../types/Pricing';
import { SupportedCurrency } from '../types/SubscriptionPrice';

export const getYearlyCharge = ({
  amount,
  currency,
}: MonetaryValue): MonetaryValue => {
  return {
    currency,
    amount: amount * 12,
  };
};

/** @knipignore - used in website */
// Returns a value (or cost)
export const getPriceValueInMajorUnit = (price: MonetaryValue): number => {
  return price.amount / getMinorUnitDivisor(price.currency);
};

// See https://en.wikipedia.org/wiki/ISO_4217#Minor_unit_fractions
// and https://stripe.com/docs/currencies#zero-decimal

// TODO: Add more currencies as we support them
const MINOR_UNIT_FRACTION_DIGITS: Record<SupportedCurrency, number> = {
  USD: 2,
  GBP: 2,
  AUD: 2,
  INR: 2,
  IDR: 2,
  EUR: 2,
  BRL: 2,
  CAD: 2,
  MXN: 2,
  PHP: 2,
};

const getMinorUnitFractionDigits = (currency: SupportedCurrency): number => {
  // fallback to USD unit if currency is not supported
  return MINOR_UNIT_FRACTION_DIGITS[currency] ?? MINOR_UNIT_FRACTION_DIGITS.USD;
};

/**
 * Helper function to get the minor unit divisor for a given currency.
 *
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR').
 * @returns {number} - The minor unit divisor for the currency.
 *
 * This function returns the divisor to convert minor units (e.g., cents) to major units (e.g., dollars).
 * For most currencies, the divisor is 100 (e.g., 100 cents = 1 dollar).
 */
const getMinorUnitDivisor = (currency: SupportedCurrency): number => {
  return Math.pow(10, getMinorUnitFractionDigits(currency));
};

// See https://stripe.com/docs/currencies#minimum-and-maximum-charge-amounts
// TODO: Add more currencies as we support them
const MINIMUM_CHARGE: Record<SupportedCurrency, number> = {
  USD: 50, // 50 cents
  GBP: 30, // 30 pence
  AUD: 50, // 50 cents
  INR: 50, // 50 paise
  IDR: 50, // 50 cents
  EUR: 50, // 50 cents
  BRL: 50, // 50 centavos
  CAD: 50, // 50 cents
  MXN: 10, // 10 peso
  PHP: 50, // 50 cents
} as const;

export const getMinimumCharge = (currency: SupportedCurrency): number => {
  return MINIMUM_CHARGE[currency] ?? MINIMUM_CHARGE.USD; // fallback to USD minimum charge
};

export type NotationType = 'standard' | 'compact';

/**
 * Formats a monetary value into a localized currency string.
 *
 * @param {MonetaryValue} value - The monetary value to format, including the amount and currency. Amount should be in the minor unit (e.g., cents).
 * @param {Object} [options] - Optional formatting options.
 * @param {number} [options.fractionDigits=2] - The number of fraction digits to display.
 * @param {string} [options.locale='en-US'] - The locale to use for formatting.
 * @param {NotationType} [options.notation='standard'] - The notation style to use for formatting. It is 'standard' by default. Use 'compact' for shortening large values.
 * @returns {string} - The formatted currency string.
 *
 * This function takes a monetary value and formats it into a localized currency string based on the provided options.
 * It supports customizing the number of fraction digits, locale, and notation style. For Indonesian Rupiah (IDR),
 * it uses the 'compact' notation to shorten large values (e.g., 1000 -> 1K, 1000000 -> 1M).
 */
export const formatMonetaryValue = (
  value: MonetaryValue,
  {
    fractionDigits = 2,
    locale = 'en-US',
    notation = 'standard',
  }: { fractionDigits?: number; locale?: string; notation?: NotationType } = {}
): string => {
  // Convert the amount to the major unit by dividing by the minor unit divisor
  const amount = value.amount / getMinorUnitDivisor(value.currency);

  // Format the amount into a localized currency string
  return amount.toLocaleString(locale, {
    style: 'currency',
    currency: value.currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    notation: value.currency === 'IDR' ? 'compact' : notation, // 'compact' is used to shorten values for long currencies like IDR (eg. 1000 -> 1K, 10000 -> 10K, 1000000 -> 1M)
  });
};
